import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { EntityKey } from '@/components/entities/registry';
import { ColorPreset } from '@/components/operations/types';

export type ChartBackground = 'white' | 'transparent' | ColorPreset;
import {
  ApiSavedDashboard,
  createSavedDashboardApi,
  deleteSavedDashboardApi,
  listSavedDashboardsApi,
  updateSavedDashboardApi,
} from '@/lib/savedViewsApi';

export interface DashboardColumn {
  id: string;
  entityKey: EntityKey;
  viewId: string;
  title?: string;
  // -- Legacy positioning (kept for backward-compat reads) --
  width?: number;
  height?: number;
  row?: number;
  // -- New grid positioning (0-based) --
  /** Grid column start (0..GRID_COLS-1). */
  x?: number;
  /** Grid row start (0..N). */
  y?: number;
  /** Grid column span. */
  w?: number;
  /** Grid row span. */
  h?: number;
  /** Optional tile color theme (border + header tint). */
  color?: ColorPreset | null;
  /**
   * Per-tile column width overrides. Merged over the saved view's own widths
   * so the same view can render at different sizes in different tiles.
   */
  columnWidths?: Record<string, number>;
  /** Chart-only: background fill for chart tiles. Defaults to white. */
  chartBg?: ChartBackground;
}

export const GRID_COLS = 12;
export const GRID_ROW_PX = 64;
export const GRID_GAP_PX = 8;
export const DEFAULT_GRID_W = 4;
export const DEFAULT_GRID_H = 5;

export interface DashboardView {
  id: string;
  name: string;
  columns: DashboardColumn[];
  ownerId: string;
  ownerEmail: string;
  isShared: boolean;
  isSystem: boolean;
  updatedAt?: string;
}

export const DEFAULT_COLUMN_WIDTH = 3;
export const MIN_COLUMN_WIDTH = 1;
export const MAX_COLUMN_WIDTH = 10;
const WIDTH_UNIT_PX = 100;
const WIDTH_BASE_PX = 80;

export function columnWidthPx(width: number | undefined): number {
  const w = width ?? DEFAULT_COLUMN_WIDTH;
  return w * WIDTH_UNIT_PX + WIDTH_BASE_PX;
}

export const DEFAULT_COLUMN_HEIGHT = 5;
export const MIN_COLUMN_HEIGHT = 2;
export const MAX_COLUMN_HEIGHT = 12;
const HEIGHT_UNIT_PX = 80;
const HEIGHT_BASE_PX = 120;

export function columnHeightPx(height: number | undefined): number {
  const h = height ?? DEFAULT_COLUMN_HEIGHT;
  return h * HEIGHT_UNIT_PX + HEIGHT_BASE_PX;
}

function fromApi(d: ApiSavedDashboard): DashboardView {
  const raw = (d.columns as any[]).map((c: any) => ({
    id: c.id,
    entityKey: (c.entityKey ?? 'operations') as EntityKey,
    viewId: c.viewId,
    title: c.title,
    width: c.width,
    height: c.height,
    row: c.row,
    x: c.x,
    y: c.y,
    w: c.w,
    h: c.h,
    color: c.color ?? null,
    columnWidths: c.columnWidths ?? undefined,
    chartBg: c.chartBg ?? undefined,
  }));
  return {
    id: d.id,
    name: d.name,
    columns: migrateColumnsToGrid(raw),
    ownerId: d.ownerId,
    ownerEmail: d.ownerEmail,
    isShared: d.isShared,
    isSystem: !!d.isSystem,
    updatedAt: d.updatedAt,
  };
}

export function useDashboardViews() {
  const { token, currentUser } = useAuth();
  const [views, setViews] = useState<DashboardView[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await listSavedDashboardsApi(token);
        if (!cancelled) setViews(data.map(fromApi));
      } catch (err) {
        console.error('Failed to load dashboards', err);
        if (!cancelled) setViews([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const createView = useCallback(
    async (name: string, columns: DashboardColumn[], isShared: boolean): Promise<DashboardView> => {
      const created = await createSavedDashboardApi(token, { name, columns, isShared });
      const view = fromApi(created);
      setViews((prev) => [...prev, view]);
      return view;
    },
    [token]
  );

  const updateView = useCallback(
    async (
      id: string,
      patch: { name?: string; columns?: DashboardColumn[]; isShared?: boolean }
    ) => {
      const updated = await updateSavedDashboardApi(token, id, patch);
      const view = fromApi(updated);
      setViews((prev) => prev.map((v) => (v.id === id ? view : v)));
      return view;
    },
    [token]
  );

  const deleteView = useCallback(
    async (id: string) => {
      await deleteSavedDashboardApi(token, id);
      setViews((prev) => prev.filter((v) => v.id !== id));
    },
    [token]
  );

  const currentUserId: string | undefined = (currentUser as any)?._id ?? (currentUser as any)?.id;

  return { views, loading, createView, updateView, deleteView, currentUserId };
}

export function columnsEqual(a: DashboardColumn[], b: DashboardColumn[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].viewId !== b[i].viewId) return false;
    if (a[i].entityKey !== b[i].entityKey) return false;
    if ((a[i].title ?? '') !== (b[i].title ?? '')) return false;
    if ((a[i].x ?? 0) !== (b[i].x ?? 0)) return false;
    if ((a[i].y ?? 0) !== (b[i].y ?? 0)) return false;
    if ((a[i].w ?? DEFAULT_GRID_W) !== (b[i].w ?? DEFAULT_GRID_W)) return false;
    if ((a[i].h ?? DEFAULT_GRID_H) !== (b[i].h ?? DEFAULT_GRID_H)) return false;
    if ((a[i].color ?? null) !== (b[i].color ?? null)) return false;
    if (JSON.stringify(a[i].columnWidths ?? null) !== JSON.stringify(b[i].columnWidths ?? null))
      return false;
    if ((a[i].chartBg ?? null) !== (b[i].chartBg ?? null)) return false;
  }
  return true;
}

/**
 * Convert legacy row/width/height columns to grid x/y/w/h coordinates.
 * Returns the input unchanged if every column already has grid coords.
 */
export function migrateColumnsToGrid(columns: DashboardColumn[]): DashboardColumn[] {
  const allGrid = columns.every(
    (c) => c.x !== undefined && c.y !== undefined && c.w !== undefined && c.h !== undefined
  );
  if (allGrid) return columns;

  // Group by legacy row index, then lay out left-to-right within each row.
  const byRow = new Map<number, DashboardColumn[]>();
  for (const c of columns) {
    const r = c.row ?? 0;
    if (!byRow.has(r)) byRow.set(r, []);
    byRow.get(r)!.push(c);
  }
  const out: DashboardColumn[] = [];
  let y = 0;
  for (const [, rowCols] of Array.from(byRow.entries()).sort((a, b) => a[0] - b[0])) {
    let x = 0;
    let rowHeight = DEFAULT_GRID_H;
    for (const c of rowCols) {
      const w = c.w ?? Math.max(1, Math.min(GRID_COLS, c.width ?? DEFAULT_COLUMN_WIDTH));
      const h = c.h ?? Math.max(2, c.height ?? DEFAULT_GRID_H);
      // Wrap to next row if no horizontal room left.
      if (x + w > GRID_COLS) {
        y += rowHeight;
        x = 0;
        rowHeight = DEFAULT_GRID_H;
      }
      out.push({ ...c, x, y, w, h });
      x += w;
      rowHeight = Math.max(rowHeight, h);
    }
    y += rowHeight;
  }
  return out;
}
